import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import HelpButton from '../components/HelpButton';

function currentDefaultYear() {
  const now = new Date();
  return now.getFullYear() + (now.getMonth() >= 9 ? 1 : 0);
}

export default function DrawResultPage() {
  const navigate = useNavigate();
  const [year] = useState(currentDefaultYear());
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadStatus(); }, []);

  async function loadStatus() {
    setLoading(true);
    try {
      const data = await api.getDrawStatus(year);
      setStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleOpenBox() {
    setOpening(true);
    setError('');
    try {
      const data = await api.getMyResult(year);
      // pequeña espera para que se vea la animación de apertura
      setTimeout(() => {
        setResult(data);
        setOpening(false);
        setTimeout(() => setRevealed(true), 300);
      }, 1400);
    } catch (err) {
      setOpening(false);
      setError(err.message);
    }
  }

  if (loading) {
    return <div style={styles.center}><p style={{ color: 'var(--text-muted)' }}>Cargando... 🎄</p></div>;
  }

  if (!status?.done) {
    return (
      <div style={styles.page}>
        <div className="card" style={styles.waitingCard}>
          <span style={{ fontSize: '3rem' }}>⏳</span>
          <h3 style={{ color: 'var(--gold)', margin: '0.5rem 0' }}>Todavía no hay sorteo</h3>
          <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>
            En cuanto el administrador haga el sorteo del Amigo Invisible {year}, podrás abrir tu regalo aquí.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/home')}>← Volver</button>
        <HelpButton title="🎰 Amigo Invisible">
          <p>Cuando el administrador haga el sorteo, aquí aparecerá una caja de regalo para abrir.</p>
          <p>Toca la caja para descubrir, en secreto, a quién te ha tocado regalar.</p>
          <p>🤫 Solo tú puedes ver tu resultado. Nadie más en la familia sabrá a quién te tocó, a menos que tú lo cuentes.</p>
        </HelpButton>
      </div>
      <h2 style={styles.title}>🎁 Amigo Invisible {year}</h2>

      {!result ? (
        <div style={styles.boxArea}>
          <GiftBox opening={opening} onClick={!opening ? handleOpenBox : undefined} />
          <p style={styles.hint}>
            {opening ? 'Abriendo tu regalo...' : 'Toca la caja para descubrir a quién le regalas'}
          </p>
        </div>
      ) : (
        <div style={{ ...styles.resultArea, opacity: revealed ? 1 : 0, transform: revealed ? 'scale(1)' : 'scale(0.9)' }}>
          <span style={{ fontSize: '3rem' }}>🎉</span>
          <p style={styles.resultLabel}>Le toca regalar a</p>
          <h1 style={styles.resultName}>{result.receiver.display_name}</h1>
          <p style={styles.resultNote}>¡Shhh! Es un secreto 🤫</p>
        </div>
      )}

      {error && <p className="error-msg" style={{ textAlign: 'center', marginTop: '1rem' }}>⚠ {error}</p>}
    </div>
  );
}

function GiftBox({ opening, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        display: 'flex',
        justifyContent: 'center',
        padding: '2rem 0',
      }}
    >
      <svg width="180" height="180" viewBox="0 0 200 200" style={{
        animation: opening ? 'shake 0.4s ease-in-out infinite' : 'float 3s ease-in-out infinite',
      }}>
        <defs>
          <linearGradient id="boxFront" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#c0392b" />
            <stop offset="100%" stopColor="#922b21" />
          </linearGradient>
          <linearGradient id="lidFront" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e74c3c" />
            <stop offset="100%" stopColor="#c0392b" />
          </linearGradient>
        </defs>

        {/* Cuerpo de la caja */}
        <rect x="40" y="95" width="120" height="85" rx="4" fill="url(#boxFront)" />
        <rect x="90" y="95" width="20" height="85" fill="#a83226" />

        {/* Tapa */}
        <g style={{
          transformOrigin: '100px 95px',
          animation: opening ? 'lidOpen 1.4s ease-in forwards' : 'none',
        }}>
          <rect x="30" y="70" width="140" height="30" rx="4" fill="url(#lidFront)" />
          <rect x="90" y="70" width="20" height="30" fill="#b6362a" />
          {/* Lazo */}
          <path d="M100 70 Q75 40 55 60 Q70 75 100 70 Z" fill="#f4d35e" />
          <path d="M100 70 Q125 40 145 60 Q130 75 100 70 Z" fill="#f4d35e" />
          <circle cx="100" cy="68" r="9" fill="#e8c145" />
        </g>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-6px); }
          }
          @keyframes shake {
            0%, 100% { transform: translateX(0) rotate(0deg); }
            25% { transform: translateX(-3px) rotate(-2deg); }
            75% { transform: translateX(3px) rotate(2deg); }
          }
          @keyframes lidOpen {
            0%, 40% { transform: translateY(0) rotate(0deg); opacity: 1; }
            100% { transform: translateY(-60px) rotate(-25deg); opacity: 0; }
          }
        `}</style>
      </svg>
    </div>
  );
}

const styles = {
  page: { padding: '1.5rem 1rem' },
  center: { display: 'flex', justifyContent: 'center', padding: '3rem' },
  title: { textAlign: 'center', color: 'var(--gold)', fontSize: '1.4rem', marginBottom: '1rem' },
  waitingCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '2.5rem 1.5rem',
  },
  boxArea: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  hint: { color: 'var(--text-muted)', fontSize: '0.9rem', textAlign: 'center' },
  resultArea: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '2.5rem 1.5rem',
    transition: 'all 0.5s ease',
  },
  resultLabel: { color: 'var(--text-muted)', fontSize: '0.95rem', marginTop: '0.5rem' },
  resultName: {
    color: 'var(--gold)',
    fontSize: '2.2rem',
    fontFamily: 'var(--font-display)',
    margin: '0.5rem 0',
    textAlign: 'center',
  },
  resultNote: { color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' },
};
